import { useEffect, useMemo, useState } from 'react'
import { useQuery, DocumentInput } from 'urql'
import { logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'

export type IntegrationItem = {
  id: string
  name: string
  isUsed: boolean
  labelId?: string | null
  labelName?: string | null
}

export type UseIntegrationQueryOptions<TData, TItem extends IntegrationItem> = {
  query: DocumentInput
  variables?: Record<string, unknown>
  /** Extract the array of items from the query result */
  extractItems: (data: TData) => TItem[]
  /** Optional: transform item to OptionType. Defaults to standard mapping */
  mapToOption?: (item: TItem) => OptionType
  /** Message for disabled options */
  disabledTooltip?: string
  /** Error message for logging */
  errorMessage?: string
}

export type UseIntegrationQueryReturn = {
  options: OptionType[]
  fetching: boolean
  error: Error | undefined
  refreshing: boolean
  refresh: () => void
}

/**
 * Shared hook for integration option queries.
 * Handles loading, error notification, refresh state, and option mapping.
 */
export function useIntegrationQuery<TData, TItem extends IntegrationItem = IntegrationItem>({
  query,
  variables,
  extractItems,
  mapToOption,
  disabledTooltip = `This item is connected to another location`,
  errorMessage = `Failed to load options`,
}: UseIntegrationQueryOptions<TData, TItem>): UseIntegrationQueryReturn {
  const [refreshing, setRefreshing] = useState(false)

  const [{ data, fetching, error }, reexecuteQuery] = useQuery<TData>({
    query,
    variables,
    requestPolicy: 'network-only',
  })

  useEffect(() => {
    if (error) logError(errorMessage, error)
    if (!fetching) setRefreshing(false)
  }, [error, fetching, errorMessage])

  const options = useMemo<OptionType[]>(() => {
    if (!data) return []
    const items = extractItems(data)
    if (!items) return []

    const defaultMapper = (item: TItem): OptionType => ({
      id: item.id,
      name: item.name,
      value: item.id,
      meta: {
        isUsed: item.isUsed,
        tooltipForDisabledOption: disabledTooltip,
      },
    })

    return items.map(mapToOption ?? defaultMapper)
  }, [data, extractItems, mapToOption, disabledTooltip])

  const refresh = () => {
    setRefreshing(true)
    reexecuteQuery({ requestPolicy: `network-only` })
  }

  return {
    options,
    fetching,
    error,
    refreshing,
    refresh,
  }
}

export type UseSelectedOptionOptions<TItem extends IntegrationItem> = {
  items: TItem[] | undefined
  currentId: string | undefined
  /** Optional: transform item to OptionType for selected value */
  mapToOption?: (item: TItem) => OptionType
}

/**
 * Hook to set selected option when data loads and we have a current value.
 */
export function useSelectedOption<TItem extends IntegrationItem>({
  items,
  currentId,
  mapToOption,
}: UseSelectedOptionOptions<TItem>): OptionType | undefined {
  const [selectedOption, setSelectedOption] = useState<OptionType | undefined>(undefined)

  useEffect(() => {
    if (!items || !currentId) return

    const item = items.find((i) => i.id === currentId)
    if (!item) return

    const defaultMapper = (i: TItem): OptionType => ({
      id: i.id,
      name: i.name,
      value: i.id,
    })

    setSelectedOption((mapToOption ?? defaultMapper)(item))
  }, [items, currentId, mapToOption])

  return selectedOption
}
